import {
  ArrowsClockwise,
  CaretDown,
  Check,
  Copy,
  Eye,
  MapPin,
  Printer,
  UploadSimple,
} from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { type AdminHouseholdDetail, useExportHousehold, useUpdateHousehold } from "@/lib/api";
import { cn } from "@/lib/utils";

import { ExportJsonDialog } from "./export-json-dialog";
import { InlineEdit } from "./inline-edit";
import { KitSheetDialog } from "./kit-sheet-dialog";
import { RegenerateCodeDialog } from "./regenerate-code-dialog";
import {
  HOUSEHOLD_STATUS_LABELS,
  HOUSEHOLD_STATUS_ORDER,
  type HouseholdStatus,
  householdStatusLabel,
} from "./status";

interface CommandBandProps {
  household: AdminHouseholdDetail;
}

const STATUS_STYLES: Record<string, string> = {
  new: "bg-surface-subtle text-muted",
  configuring: "bg-sky/10 text-sky",
  ready_to_ship: "bg-warning/10 text-warning",
  shipped: "bg-accent/10 text-accent-pressed",
  ready_for_handoff: "bg-success/10 text-success",
  support_needed: "bg-destructive/10 text-destructive",
};

/** Renders the household access code with a thin visual gap between halves. */
function formatAccessCode(code: string | null): string {
  if (!code) {
    return "——————";
  }
  if (/^\d{6}$/.test(code)) {
    return `${code.slice(0, 3)} ${code.slice(3)}`;
  }
  return code;
}

function formatAddressLine(household: AdminHouseholdDetail): string {
  const parts = [
    household.address ?? undefined,
    [household.postcode, household.city].filter(Boolean).join(" ") || undefined,
  ].filter((part): part is string => Boolean(part && part.trim() !== ""));
  const line = parts.join(", ");
  return household.country_code ? [line, household.country_code].filter(Boolean).join(" · ") : line;
}

/**
 * Top-of-page command band: editable household identity, status, access
 * code, and the handoff readiness meter that opens the export dialog. Owns
 * the `useExportHousehold` mutation so the dialog stays presentational.
 */
export function CommandBand({ household }: CommandBandProps) {
  const updateMutation = useUpdateHousehold();
  const exportMutation = useExportHousehold();
  const [codeCopied, setCodeCopied] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [kitSheetOpen, setKitSheetOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);

  const statusClass = STATUS_STYLES[household.status] ?? STATUS_STYLES.new;
  const isReady = household.readiness_percent >= 100 && household.readiness_issues.length === 0;

  const handleSaveName = async (name: string) => {
    if (!name.trim()) {
      throw new Error("Name is required.");
    }
    await updateMutation.mutateAsync({ id: household.id, body: { name } });
  };

  const handleChangeStatus = (status: HouseholdStatus) => {
    if (status === household.status) {
      return;
    }
    updateMutation.mutate({ id: household.id, body: { status } });
  };

  const handleCopyCode = () => {
    if (!household.access_code) {
      return;
    }
    void navigator.clipboard?.writeText(household.access_code);
    setCodeCopied(true);
    window.setTimeout(() => setCodeCopied(false), 2000);
  };

  const openExportDialog = () => {
    setExportOpen(true);
    void exportMutation.mutateAsync(household.id);
  };

  return (
    <div className="bg-card shadow-elevation mb-5 rounded-md p-6">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="text-muted type-label">Household</div>
          <h1 className="text-primary my-1 text-[30px] leading-[1.1] font-semibold tracking-[-0.02em]">
            <InlineEdit value={household.name} onSave={handleSaveName} ariaLabel="Household name">
              {household.name}
            </InlineEdit>
          </h1>
          <div className="text-muted flex flex-wrap items-center gap-1.5 text-[15px]">
            <MapPin className="size-[15px] shrink-0" weight="light" />
            {formatAddressLine(household) || "No address on file"}
            <span
              className={cn(
                "focus-within:ring-ring relative inline-flex items-center gap-1 rounded-sm px-2.5 py-0.5 text-[13px] font-semibold focus-within:ring-2",
                updateMutation.isPending && "opacity-60",
                statusClass,
              )}
            >
              {householdStatusLabel(household.status)}
              <CaretDown weight="bold" className="size-2.5 shrink-0 opacity-70" />
              {/* Transparent native select overlays the pill for the dropdown —
                  the visible label sizes the pill to the current value (a native
                  select would size to its widest option). */}
              <select
                aria-label="Household status"
                value={household.status}
                disabled={updateMutation.isPending}
                onChange={(e) => handleChangeStatus(e.target.value as HouseholdStatus)}
                className="absolute inset-0 cursor-pointer rounded-sm opacity-0 outline-none disabled:cursor-not-allowed"
              >
                {HOUSEHOLD_STATUS_ORDER.map((value) => (
                  <option key={value} value={value}>
                    {HOUSEHOLD_STATUS_LABELS[value]}
                  </option>
                ))}
              </select>
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-0.5 text-[13px] font-semibold before:size-[7px] before:rounded-full before:bg-current",
                household.preconfig_ready
                  ? "bg-success/10 text-success"
                  : "bg-warning/10 text-warning",
              )}
            >
              {household.preconfig_ready
                ? "Pre-ship ready"
                : `Pre-ship: ${household.preconfig_missing.length} to do`}
            </span>
          </div>
        </div>

        <div className="border-border-subtle bg-background flex items-center gap-2.5 rounded-xl border py-2 pr-2 pl-3.5">
          <div>
            <div className="text-muted type-label">Access code</div>
            <div className="text-primary font-mono text-xl font-semibold tracking-[0.22em]">
              {formatAccessCode(household.access_code)}
            </div>
          </div>
          <button
            type="button"
            title="Copy"
            onClick={handleCopyCode}
            className="border-border text-muted hover:text-primary hover:border-border-strong bg-card grid size-[30px] shrink-0 cursor-pointer place-items-center rounded-lg border transition-colors"
          >
            {codeCopied ? (
              <Check className="size-[15px]" weight="light" />
            ) : (
              <Copy className="size-[15px]" weight="light" />
            )}
          </button>
          <button
            type="button"
            title="Issue a new code"
            onClick={() => setRegenerateOpen(true)}
            className="border-border text-muted hover:text-primary hover:border-border-strong bg-card grid size-[30px] shrink-0 cursor-pointer place-items-center rounded-lg border transition-colors"
          >
            <ArrowsClockwise className="size-[15px]" weight="light" />
          </button>
        </div>
      </div>

      <div className="border-border-subtle mt-5 flex flex-wrap items-center gap-6 border-t pt-[18px]">
        <div className="min-w-[240px] flex-1">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-primary flex items-center gap-2 text-sm font-semibold">
              Handoff payload
            </span>
            <span className="text-primary font-mono text-sm font-semibold tabular-nums">
              {household.readiness_percent}%
            </span>
          </div>
          <div className="bg-surface-subtle h-2 overflow-hidden rounded-sm">
            <div
              className="bg-accent h-full transition-[width]"
              style={{ width: `${household.readiness_percent}%` }}
            />
          </div>
          {isReady ? (
            <p className="text-success mt-[7px] text-[13px] font-semibold">Ready to hand off</p>
          ) : (
            <p className="text-muted mt-[7px] text-[13px]">
              <b className="text-warning font-semibold">
                {household.readiness_issues.length} items to confirm
              </b>{" "}
              before this home&apos;s <span className="font-mono">setupData</span> JSON can be
              handed off.
            </p>
          )}
        </div>
        <div className="flex gap-2.5">
          <Button type="button" variant="outline" onClick={() => setKitSheetOpen(true)}>
            <Printer className="size-4" weight="light" />
            Kit sheet
          </Button>
          <Button type="button" variant="outline" onClick={openExportDialog}>
            <Eye className="size-4" weight="light" />
            Preview JSON
          </Button>
          <Button type="button" onClick={openExportDialog}>
            <UploadSimple className="size-4" weight="light" />
            Export handoff
          </Button>
        </div>
      </div>

      <KitSheetDialog household={household} open={kitSheetOpen} onOpenChange={setKitSheetOpen} />

      <RegenerateCodeDialog
        householdId={household.id}
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
      />

      <ExportJsonDialog
        householdName={household.name}
        payload={exportMutation.data}
        isLoading={exportMutation.isPending}
        readinessPercent={household.readiness_percent}
        readinessIssues={household.readiness_issues}
        open={exportOpen}
        onOpenChange={setExportOpen}
      />
    </div>
  );
}
